import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  bandsToValues,
  MAX_TIER_BANDS,
  MIN_TIER_BANDS,
  validateTierBandValues,
  valuesToBands,
} from '../../shared/tier-bands-form'

export interface TierBandsEditorProps {
  bands: number[]
  isDefault: boolean
  saving: boolean
  apiError?: string | null
  onSave: (bands: number[]) => void
}

export default function TierBandsEditor({
  bands,
  isDefault,
  saving,
  apiError,
  onSave,
}: TierBandsEditorProps) {
  const { formatMessage } = useIntl()
  const [values, setValues] = useState<string[]>(() => bandsToValues(bands))

  const errors = useMemo(() => validateTierBandValues(values), [values])
  const dirty = values.join('|') !== bands.join('|')
  const valid = Object.keys(errors).length === 0

  const errorText = (i: number) => {
    const code = errors[i]
    if (code === undefined) {
      return undefined
    }

    return formatMessage(
      { id: `avatarStudio.tiers.errors.${code}` },
      { tier: i, previous: i - 1 },
    )
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 420 }}>
      <Alert severity="info">{formatMessage({ id: 'avatarStudio.tiers.publishNote' })}</Alert>

      {isDefault && !dirty && (
        <Alert severity="info">{formatMessage({ id: 'avatarStudio.tiers.defaultNote' })}</Alert>
      )}

      {apiError && <Alert severity="error">{apiError}</Alert>}

      <Stack spacing={1.25}>
        {values.map((value, i) => (
          <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
            <Chip
              label={formatMessage({ id: 'avatarStudio.tiers.tierChip' }, { tier: i })}
              size="small"
              color={i >= MIN_TIER_BANDS ? 'secondary' : 'default'}
              sx={{ mt: 1, minWidth: 44, fontFamily: 'monospace' }}
            />
            <TextField
              size="small"
              type="number"
              fullWidth={true}
              label={formatMessage({ id: 'avatarStudio.tiers.startsAt' }, { tier: i })}
              value={value}
              disabled={i === 0 || saving}
              inputProps={{ min: 0, step: 1 }}
              error={Boolean(errors[i])}
              helperText={errorText(i)}
              // tslint:disable-next-line: jsx-no-lambda
              onChange={(e) =>
                setValues((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
              }
            />
            {i === values.length - 1 && values.length > MIN_TIER_BANDS ? (
              <Tooltip title={formatMessage({ id: 'avatarStudio.tiers.removeTier' })}>
                <IconButton
                  aria-label={formatMessage({ id: 'avatarStudio.tiers.removeTier' })}
                  size="small"
                  disabled={saving}
                  sx={{ mt: 0.5 }}
                  // tslint:disable-next-line: jsx-no-lambda
                  onClick={() => setValues((prev) => prev.slice(0, -1))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          startIcon={<AddOutlinedIcon />}
          disabled={saving || values.length >= MAX_TIER_BANDS}
          // tslint:disable-next-line: jsx-no-lambda
          onClick={() => setValues((prev) => [...prev, ''])}
        >
          {formatMessage({ id: 'avatarStudio.tiers.addTier' })}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {formatMessage(
            { id: 'avatarStudio.tiers.progressionNote' },
            { max: MAX_TIER_BANDS - 1 },
          )}
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {formatMessage({ id: 'avatarStudio.tiers.topTierNote' })}
      </Typography>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          disabled={saving || !dirty}
          // tslint:disable-next-line: jsx-no-lambda
          onClick={() => setValues(bandsToValues(bands))}
        >
          {formatMessage({ id: 'avatarStudio.tiers.reset' })}
        </Button>
        <Button
          variant="contained"
          disabled={saving || !valid || !dirty}
          // tslint:disable-next-line: jsx-no-lambda
          onClick={() => onSave(valuesToBands(values))}
        >
          {formatMessage({
            id: saving ? 'avatarStudio.tiers.saving' : 'avatarStudio.tiers.save',
          })}
        </Button>
      </Stack>
    </Stack>
  )
}
